import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Typography,
  Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import COLORS from '../../resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';

// ----------------- Set these values as per the requirements on the DAY OF THE RELEASE -----------------
// UTC-based global window (same instant for all users)

const NOTIFICATION_START_DATE: Date = new Date('2026-05-14T00:00:00Z'); // YYYY-MM-DD format
const NOTIFICATION_END_DATE: Date = new Date('2026-05-28T23:59:59Z'); // YYYY-MM-DD format

const SESSION_STORAGE_KEY = 'production-feature-notification-dismissed';

const ProductionFeatureNotification: React.FC = () => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { t } = useTranslation();

  // Check if current time is within the UTC notification window
  const isWithinDateRange = (): boolean => {
    const now = Date.now();
    return (
      now >= NOTIFICATION_START_DATE.getTime() &&
      now <= NOTIFICATION_END_DATE.getTime()
    );
  };

  // Check if notification was dismissed in this session
  const isDismissed = (): boolean => {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
  };

  useEffect((): void => {
    // Show notification if:
    // 1. In Production environment (DEV allowed temporarily for testing)
    // 2. Within UTC date range
    // 3. Not dismissed in current session
    if (
      (import.meta.env.PROD || import.meta.env.DEV) &&
      isWithinDateRange() &&
      !isDismissed()
    ) {
      setIsVisible(true);
    }
  }, []);

  const handleClose = (): void => {
    setIsVisible(false);
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
  };

  if (!isVisible) return null;

  return (
    <Fade in={isVisible} timeout={500}>
      <Card
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 60,
          width: 380,
          maxWidth: 'calc(100vw - 40px)',
          zIndex: 1300,
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5)',
          backgroundColor: COLORS.WHITE,
        }}
      >
        <CardHeader
          title={
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                fontSize: '18px',
                textTransform: 'uppercase',
                color: COLORS.WHITE,
              }}
            >
              {t(StringKeys.ATTENTION_USERS)} !
            </Typography>
          }
          action={
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: COLORS.WHITE }}
            >
              <CloseIcon />
            </IconButton>
          }
          sx={{
            backgroundColor: COLORS.SAFEGUARD_BLUE,
            padding: '10px 12px',
          }}
        />
        <CardContent
          sx={{
            padding: '16px',
            '&:last-child': {
              paddingBottom: '16px',
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: 'black',
              lineHeight: 1.6,
              fontSize: '14px',
            }}
          >
            {t(StringKeys.NEW_FEATURE_NOTIFICATION)}
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default ProductionFeatureNotification;
